/**
 * Traducciones ES -> EN de los modales de proceso/operación, el historial, la pantalla de gobierno
 * y el modal de confirmación. La clave es la cadena en español EXACTA (mismos acentos, mayúsculas,
 * signos y `{x}` de interpolación); el valor es su inglés natural.
 */
export const MODALES: Record<string, string> = {
  // --- ModalProceso: tabla TX (verbo / ok / okSub por acción) ---
  "Añadiendo fondos": "Adding funds",
  "Fondos añadidos": "Funds added",
  "Tu posición creció; el objetivo a recuperar subió con ella.": "Your position grew; the amount to recover rose with it.",
  "Cargando la bolsa de gas": "Topping up the gas budget",
  "Gas cargado": "Gas topped up",
  "El robot ya puede reajustar.": "The robot can rebalance again.",
  "Devolviendo el gas": "Returning the gas",
  "Gas devuelto": "Gas returned",
  "Lo que no se gastó ya está en tu wallet.": "Whatever wasn't spent is back in your wallet.",
  "Apartando a la reserva": "Setting aside to the reserve",
  "Reserva apartada": "Reserve set aside",
  "El robot la irá soltando según caiga el precio.": "The robot will release it as the price falls.",
  "Cobrando tus ganancias": "Collecting your profits",
  "Ganancias cobradas": "Profits collected",
  "Ya están en tu wallet. Tu capital y tu posición no se tocaron.": "They're in your wallet now. Your capital and position were left untouched.",
  "Reinvirtiendo tus ganancias": "Reinvesting your profits",
  "Ganancias reinvertidas": "Profits reinvested",
  "Volvieron a la posición a trabajar.": "They went back into the position to work.",
  "Sacando esa parte": "Withdrawing that part",
  "Retirado a tu wallet": "Withdrawn to your wallet",
  "El resto sigue trabajando.": "The rest keeps working.",
  "Volviendo al rango estrecho": "Returning to the tight range",
  "De vuelta al rango estrecho": "Back to the tight range",
  "Se re-centró tu posición al ancho base y tu activo no se vendió.": "Your position was re-centered to the base width and your asset wasn't sold.",
  "Cerrando el vault": "Closing the vault",
  "Vault cerrado": "Vault closed",
  "Tienes todo en tu wallet. Puedes volver a abrirlo cuando quieras.": "Everything is in your wallet. You can reopen it whenever you like.",
  "Recargando la cuota": "Refilling the quota",
  "Cuota recargada": "Quota refilled",
  "El robot vuelve a tener reajustes disponibles.": "The robot has rebalances available again.",
  "Guardando el rango": "Saving the range",
  "Rango guardado": "Range saved",
  "El robot lo usará en el próximo reajuste.": "The robot will use it on the next rebalance.",
  "Guardando los guardias": "Saving the guards",
  "Guardias guardados": "Guards saved",
  "Tu vault se protege mejor contra manipulación de precios.": "Your vault is better protected against price manipulation.",
  "Guardando la recuperación": "Saving the recovery settings",
  "Recuperación guardada": "Recovery settings saved",
  "El robot manejará las caídas como configuraste.": "The robot will handle drops the way you set it up.",
  "Guardando el colchón": "Saving the cushion",
  "Colchón guardado": "Cushion saved",
  "El rango de recuperación apuntará más alto.": "The recovery range will aim higher.",
  "Guardando la reinyección": "Saving the reinjection settings",
  "Reinyección guardada": "Reinjection settings saved",
  "El robot soltará la reserva según tus reglas.": "The robot will release the reserve according to your rules.",
  "Cambiando la reinversión": "Changing reinvestment",
  "Reinversión actualizada": "Reinvestment updated",
  "Las ganancias futuras seguirán tu nueva regla.": "Future profits will follow your new rule.",
  "Actualizando el robot": "Updating the robot",
  "Hecho": "Done",
  "El estado del robot ha cambiado.": "The robot's status has changed.",
  "Cambiando el operador": "Changing the operator",
  "Operador cambiado": "Operator changed",
  "La nueva dirección puede reajustar tu posición.": "The new address can rebalance your position.",
  "Deteniendo el robot": "Stopping the robot",
  "Robot detenido": "Robot stopped",
  "El vault sigue siendo tuyo; nadie lo reajusta hasta que pongas operador.": "The vault is still yours; no one rebalances it until you set an operator.",
  "Actualizando la lógica": "Updating the logic",
  "Lógica actualizada": "Logic updated",
  "El vault ejecuta la versión nueva del código.": "The vault runs the new version of the code.",
  // fallback genérico de TX
  "Procesando": "Processing",
  "Listo": "Done",
  "La operación se completó.": "The operation completed.",

  // --- ModalProceso / ModalOperacion: cabeceras y estados ---
  "Dando permiso al token": "Approving the token",
  "Primero autorizas al vault a mover tu token. Es la primera de dos firmas — confírmala en tu wallet.": "First you authorize the vault to move your token. It's the first of two signatures — confirm it in your wallet.",
  "Ahora firma la operación en tu wallet. Nada se mueve hasta que la des.": "Now sign the operation in your wallet. Nothing moves until you do.",
  "Firmado y enviado. Esperando a que la cadena lo confirme…": "Signed and sent. Waiting for the chain to confirm it…",
  "En marcha": "In progress",
  "No se pudo completar": "Couldn't complete",
  "Firma 1 de 2": "Signature 1 of 2",
  "Firma 2 de 2": "Signature 2 of 2",
  "Firma en tu wallet": "Sign in your wallet",
  "Confirmando en la cadena…": "Confirming on chain…",
  "Ver transacción en el explorador ↗": "View transaction on the explorer ↗",

  // --- Historial: filtros ---
  "Todo": "All",
  "Tu dinero": "Your money",
  "El robot": "The robot",
  "Ajustes": "Settings",
  "Avisos": "Alerts",

  // --- Historial: filas del timeline (tipo / nota / flujo) ---
  "nada": "nothing",
  "Piscina abierta": "Pool opened",
  "centrada en {precio} {unidad} · objetivo a recuperar {ref}": "centered at {precio} {unidad} · amount to recover {ref}",
  "rango inicial": "initial range",
  "Reajuste #{n}": "Rebalance #{n}",
  "Reajuste del rango": "Range rebalance",
  "el precio se había salido; el robot movió el rango": "the price had moved out; the robot shifted the range",
  "Recuperación": "Recovery",
  "topado por tu límite de estiramiento — no llegó a cubrir todo": "capped by your stretch limit — it didn't cover everything",
  "en vez de re-centrar y cristalizar la pérdida, se estiró hacia donde el precio tiene que volver": "instead of re-centering and locking in the loss, it stretched toward where the price has to return",
  "rango sesgado": "skewed range",
  "para devolver {ref}": "to return {ref}",
  "Reinyección (DCA)": "Reinjection (DCA)",
  "el precio bajó lo bastante para comprar otro tramo · quedan {ref} en reserva": "the price dropped enough to buy another tranche · {ref} left in reserve",
  "soltó {ref}": "released {ref}",
  "Comisiones cobradas": "Fees collected",
  "apartadas para ti · ya sin la comisión de Keellabs": "set aside for you · Keellabs fee already deducted",
  "reinvertidas en la posición": "reinvested into the position",
  "Comisión de Keellabs": "Keellabs fee",
  "el 10% de lo que generó la posición, nunca del capital": "10% of what the position generated, never of the capital",
  "Depósito": "Deposit",
  "entró dinero tuyo al vault": "your money entered the vault",
  "Retirada a tu wallet": "Withdrawal to your wallet",
  "salió de la posición": "left the position",
  "Ganancias retiradas": "Profits withdrawn",
  "a tu wallet": "to your wallet",
  "volvieron a la posición": "went back into the position",
  "Barrido": "Sweep",
  "capital que estaba parado y volvió a la posición": "capital that was idle and returned to the position",
  "Gas cobrado": "Gas charged",
  "el robot adelantó el gas de red y se reembolsó de tu bolsa": "the robot fronted the network gas and reimbursed itself from your budget",
  "Reserva recargada": "Reserve topped up",
  "reserva total {ref}": "total reserve {ref}",
  "el dueño adoptó una versión nueva del código · {hash}…": "the owner adopted a new version of the code · {hash}…",
  "Aviso: queda poco gas": "Alert: little gas left",
  "con lo que queda no salen ni tres operaciones más": "what's left won't cover even three more operations",
  "Aviso: la bolsa no cubrió el gas": "Alert: the budget didn't cover the gas",
  "pidió {want}, pagó {paid} · la diferencia la puso el robot": "requested {want}, paid {paid} · the robot covered the difference",
  "Robot pausado": "Robot paused",
  "Robot reanudado": "Robot resumed",
  "lo hizo el dueño": "done by the owner",
  "Ajustes cambiados": "Settings changed",
  "los cambió el dueño": "changed by the owner",
  "Evento": "Event",

  // --- Historial: NOMBRE_MOTIVO (gastos de gas) ---
  "Barrido del capital suelto": "Sweep of idle capital",
  "Cobro de comisiones": "Fee collection",
  "Otra operación del robot": "Another robot operation",

  // --- Historial: estados de carga/error/vacío ---
  "No pude leer el historial.": "I couldn't read the history.",
  "Todavía no ha pasado nada en este vault.": "Nothing has happened in this vault yet.",

  // --- Historial: sección Gastos ---
  "Gastos": "Costs",
  "cobrado": "collected",
  "gas": "gas",
  "comisión": "fee",
  "tu ganancia real": "your real profit",
  "Cada vez que el robot actúa, adelanta el gas de red y se reembolsa de tu bolsa. Aquí, por qué operación se fue.": "Every time the robot acts, it fronts the network gas and reimburses itself from your budget. Here's where it went, by operation.",
  "La apertura no cuenta": "The opening doesn't count",
  "esa la pagaste tú.": "you paid for that one.",
  "No pude leer los cobros de gas.": "I couldn't read the gas charges.",
  "Todavía no se ha cobrado nada de la bolsa.": "Nothing has been charged from the budget yet.",
  "Operación": "Operation",
  "Veces": "Times",
  "Total": "Total",
  "Media": "Average",

  // --- Historial: sección Qué ha pasado ---
  "Qué ha pasado en la piscina": "What has happened in the pool",
  "Abrió": "Opened",
  "reajuste": "rebalance",
  "reajustes": "rebalances",
  "recuperación": "recovery",
  "recuperaciones": "recoveries",
  "Ahora": "Now",
  "✓ en rango": "✓ in range",
  "⚠ fuera": "⚠ out of range",
  "{n} en 1 tx": "{n} in 1 tx",
  "bloque {bloque} ↗": "block {bloque} ↗",

  // --- Historial: cuando() fechas relativas ---
  "fecha desconocida": "unknown date",
  "hace un momento": "a moment ago",
  "hace {n} min": "{n} min ago",
  "hace {n} h": "{n} h ago",
  "hace {n} días": "{n} days ago",

  // --- Gobierno: cuenta atrás ---
  "ya": "now",
  "{d} d {h} h {m} min": "{d}d {h}h {m}min",
  "{h} h {m} min": "{h}h {m}min",
  "{m} min {s} s": "{m}min {s}s",
  "{m} min": "{m}min",

  // --- Gobierno: estado del modal de operación ---
  "Anunciando el cambio": "Announcing the change",
  "Aplicando el cambio": "Applying the change",
  "Confírmalo en tu wallet. Se registra el anuncio en el timelock; nada cambia hasta pasado el plazo.": "Confirm it in your wallet. The announcement is recorded in the timelock; nothing changes until the delay passes.",
  "Confírmalo en tu wallet. Se aplica el cambio ya anunciado.": "Confirm it in your wallet. The already-announced change is applied.",
  "Cambio anunciado": "Change announced",
  "Cambio aplicado": "Change applied",
  "Vuelve cuando pase el plazo para aplicarlo.": "Come back when the delay passes to apply it.",
  "La factory ya publica la lógica nueva. Los vaults nuevos nacerán con ella.": "The factory now publishes the new logic. New vaults will be born with it.",

  // --- Gobierno: pantalla principal ---
  "Leyendo el gobierno de {red}…": "Reading the governance of {red}…",
  "No pude leer el gobierno de esta red.": "I couldn't read the governance of this network.",
  "← Volver": "← Back",
  "Gobierno": "Governance",
  "Esta wallet no gobierna la factory de {red}. Manda el timelock": "This wallet doesn't govern the {red} factory. The timelock is in charge",
  "y su dueño es": "and its owner is",
  "Puedes ver igualmente lo que hay anunciado: para eso está el retardo.": "You can still see what's been announced: that's what the delay is for.",
  "Todo cambio de gobierno se": "Every governance change is",
  "anuncia": "announced",
  "espera": "waits",
  "y solo entonces se aplica. El plazo no se puede acortar: es lo que permite a cualquier dueño ver venir el cambio y sacar su dinero antes.": "and only then is applied. The delay can't be shortened: it's what lets any owner see the change coming and pull their money out first.",
  "Timelock (el admin)": "Timelock (the admin)",
  "Implementación publicada hoy": "Implementation published today",
  "Lógica publicada hoy": "Logic published today",
  "Anunciar un cambio nuevo": "Announce a new change",
  "KeelVault (implementación) nueva": "New KeelVault (implementation)",
  "KeelRebalancer (lógica) nueva": "New KeelRebalancer (logic)",
  "Las dos salen del despliegue": "Both come from the deployment",
  "Tienen que ser": "They must be",
  "idénticas": "identical",
  "al anunciar y al aplicar: el identificador de la propuesta es el hash de la llamada, así que un carácter distinto es otra propuesta.": "when announcing and when applying: the proposal's identifier is the hash of the call, so a single different character is a different proposal.",

  // --- Gobierno: aviso de contratos que no son los nuestros ---
  "Están cambiadas de sitio.": "They're swapped.",
  "Lo que pusiste como implementación responde a": "What you entered as the implementation responds to",
  "y eso solo existe en la lógica. Intercámbialas.": "and that only exists in the logic. Swap them.",
  "Eso no existe en {red}.": "That doesn't exist on {red}.",
  "Ninguna de las dos direcciones tiene un contrato aquí": "Neither address has a contract here",
  "La implementación no tiene contrato aquí": "The implementation has no contract here",
  "La lógica no tiene contrato aquí": "The logic has no contract here",
  "casi seguro son las de otra cadena.": "they're almost certainly from another chain.",
  "Cada red tiene su propio par.": "Each network has its own pair.",
  "Hay un contrato ahí, pero no es el nuestro.": "There's a contract there, but it isn't ours.",
  "La lógica no responde a CONVERT_FEE_BPS, así que no es un KeelRebalancer de esta versión.": "The logic doesn't respond to CONVERT_FEE_BPS, so it isn't a KeelRebalancer of this version.",
  "La implementación no lleva withdrawTo(bool), así que no es un KeelVault de esta versión.": "The implementation doesn't have withdrawTo(bool), so it isn't a KeelVault of this version.",
  "Direcciones de otra cadena pueden tener encima un contrato ajeno: pasa, y sin esta comprobación se publicaría igual.": "Addresses from another chain can have someone else's contract on them: it happens, and without this check it would be published anyway.",
  "Publicarlo": "Publishing it",
  "no fallaría al firmar": "wouldn't fail on signing",
  "Fallaría después, con dinero dentro.": "It would fail later, with money inside.",

  // --- Gobierno: estado de la propuesta ---
  "Esta llamada": "This call",
  "no está anunciada": "isn't announced",
  "todavía.": "yet.",
  "Anunciada. Se puede aplicar en": "Announced. It can be applied in",
  "Ya se puede": "It can now be",
  "aplicar": "applied",
  "La propuesta": "The proposal",
  "caducó": "expired",
  "hay que volver a anunciarla.": "it has to be announced again.",

  // --- Gobierno: botones de acción ---
  "1 · Anunciar el cambio": "1 · Announce the change",
  "Todavía no ha pasado el plazo": "The delay hasn't passed yet",
  "2 · Aplicarlo": "2 · Apply it",
  "Después de aplicarlo, los vaults que ya existen": "After applying it, the vaults that already exist",
  "no cambian": "don't change",
  "siguen con la lógica con la que nacieron hasta que su dueño pulse «Actualizar la lógica del vault». Lo que cambia es con qué código nacen los nuevos.": "they keep the logic they were born with until their owner clicks \"Update the vault's logic\". What changes is the code new vaults are born with.",

  // --- Gobierno: cola de pendientes ---
  "Leyendo la cola de {red}…": "Reading the queue of {red}…",
  "El RPC de {red} no me dejó recorrer todo el historial de anuncios, así que esta lista": "The RPC of {red} didn't let me walk the full announcement history, so this list",
  "puede estar incompleta": "may be incomplete",
  "Compruébalo en el explorador antes de dar por hecho que no hay nada pendiente.": "Check the explorer before assuming there's nothing pending.",
  "Anuncios pendientes": "Pending announcements",
  "No hay nada anunciado en {red}. Cuando lo haya, aparecerá aquí con el tiempo que le falte para poder aplicarse.": "Nothing is announced on {red}. When there is, it will show up here with the time left before it can be applied.",
  " · la más reciente": " · the most recent",
  " · hay una más nueva anunciada": " · a newer one is announced",
  "Caducó: hay que volver a anunciarla": "Expired: it has to be announced again",
  "Todavía no ha pasado el retardo": "The delay hasn't passed yet",
  "Aplicar": "Apply",
  "Caducó el {fecha}. Hay que": "Expired on {fecha}. It has to be",
  "volver a anunciarla": "announced again",
  "Se puede aplicar": "It can be applied",
  "Caduca en": "Expires in",
  "Faltan": "Left:",
  "se podrá aplicar el {fecha}, y hay {dias} días para hacerlo antes de que caduque.": "it can be applied on {fecha}, and there are {dias} days to do so before it expires.",

  // --- Gobierno: avisos de propuestas rivales / peligrosas ---
  "Hay": "There is",
  "otra propuesta viva más antigua": "another older live proposal",
  "que también publica lógica, con otras direcciones. No se anulan entre sí: manda la última que apliques. Esta es": "that also publishes logic, with different addresses. They don't cancel each other out: the last one you apply wins. This is",
  "la anunciada más tarde": "the one announced later",
  "deja caducar la otra.": "let the other one expire.",
  "Hay una propuesta": "There is a",
  "más reciente": "more recent",
  "que también publica lógica. Si aplicas esta": "proposal that also publishes logic. If you apply this one",
  "después": "after",
  "de aquella, vuelves atrás. Salvo que sea justo lo que quieres, deja que caduque.": "that one, you roll back. Unless that's exactly what you want, let it expire.",
  "Esa implementación": "That implementation",
  "no está en la lista que conoce la app": "isn't in the list the app knows",
  "de {red}": "of {red}",
  "Si se publica, los vaults que nazcan con ella se quedarán": "If it's published, the vaults born with it will end up",
  "sin los botones de gestión": "without the management buttons",
  "y no falla nada visible: simplemente no aparecen. Añádela a": "and nothing visibly breaks: they simply don't appear. Add it to",
  "antes de aplicar esto.": "before applying this.",
  "El destino de esta llamada": "The target of this call",
  "no es la factory de {red}": "is not the {red} factory",
  "es": "it's",
  "Míralo en el explorador antes de aplicarla.": "Check it on the explorer before applying it.",
  "Esta propuesta": "This proposal",
  "cede el control": "hands over control",
  "Después de aplicarla, esta pantalla puede dejar de mandar sobre {red}.": "After applying it, this screen may stop governing {red}.",

  // --- Confirmar ---
  "Cancelar": "Cancel",
  "Tu wallet te va a pedir la firma. Nada se mueve hasta que la des.": "Your wallet will ask you to sign. Nothing moves until you do.",

  // --- Selector de wallet (ElegirWallet) ---
  "Elige tu wallet": "Choose your wallet",
  "No detectamos ninguna wallet en este navegador. Instala MetaMask o Rabby (o activa la extensión) y recarga la página.": "We didn't detect any wallet in this browser. Install MetaMask or Rabby (or enable the extension) and reload the page.",
  "No se pudo conectar": "Couldn't connect",
};
